import React, { useState, useEffect, useCallback } from 'react';
import FteTrackingItem from '../../Domain/OutsourcedUserFte/FteTrackingItem';
import OutsourcedUserFteService from '../../Services/OutsourcedUserFte/OutsourcedUserFteService';
import ShownPanelsEnum from '../../Enums/ShownPanelsEnum';
import CrudTypesEnum from '../../Enums/CrudTypesEnum';
import FigmaButtonRow from '../../Elements/FigmaButtonRow';
import ButtonTypeEnum from '../../Enums/ButtonTypeEnum';
import ButtonColorsEnum from '../../Enums/ButtonColorsEnum';
import IconPositionEnum from '../../Enums/IconPositionEnum';
import GeneralEditWizzardBase from '../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import EditOutsourcedUserFteWindowDescription from '../User/UserAdmin/EditOutsourcedUserFteWindowDescription';
import EditFteTrackingWindowDescription from './EditFteTrackingWindowDescription';
import EditReverseIobWindowDescription, { ReverseIobItem } from './EditReverseIobWindowDescription';
import EditReverseBonusIobWindowDescription from './EditReverseBonusIobWindowDescription';
import EditChangeStatusWindowDescription, { ChangeStatusItem } from './EditChangeStatusWindowDescription';
import ErrorMessage from '../../Elements/ErrorMessage';
import TabOfDataTable from '../../Elements/DataTable/TabOfDataTable';
import FteTrackingSearchParams from './FteTrackingSearchParams';
import FteToolbar from './FteToolbar';
import Loading from '../../Elements/loading';
import useFteWarnings from './useFteWarnings';
import useMonthSelector from './useMonthSelector';
import { handleIobQueuedResponse, handleBonusIobQueuedResponse, handleReverseIobResponse } from './fteTrackingUtils';
import { canEntryBeCancelled, hasLiveBonusShare } from '../../Domain/OutsourcedUserFte/outsourcedUserFteHelpers';
import UserService from '../../Services/userService';

// The batch rule both reversals share (mirrors the backend): one reverse invoice carries one office pair, so the
// entries must share the fieldwork office and the billing-from office. Returns null after showing the error.
function createReverseIobItem(entries) {
  const fieldworkOfficeIds = [...new Set(entries.map((item) => item.FieldworkOfficeId))];
  if (fieldworkOfficeIds.length > 1) {
    ErrorMessage.SetErrorMessage('All selected entries must have the same fieldwork office.');
    return null;
  }

  // The billing-from office is the Hiring Office override, or the home office
  const fromOfficeIds = [...new Set(entries.map((item) => item.HiringOfficeId || item.UserOfficeId))];
  if (fromOfficeIds.length > 1 || !fromOfficeIds[0]) {
    ErrorMessage.SetErrorMessage('All selected entries must have the same billing office (hiring or home office).');
    return null;
  }

  // Reversed: ToOffice = the original billing-from office
  const toOfficeId = fromOfficeIds[0];
  const toOfficeName = entries[0].HiringOfficeName || entries[0].UserOfficeName || `Office #${toOfficeId}`;
  const fteEntryIds = entries.map((item) => item.OutsourcedUserFteid);
  return new ReverseIobItem(fteEntryIds, toOfficeId, toOfficeName);
}

// Wraps one of the selection handlers TabOfDataTable binds onto FteTrackingItem so the page re-renders its
// toolbar on every tick. The grid binds a fresh, unwrapped handler each time it is constructed, so this is
// called after every render and wraps only a handler that is not wrapped yet.
function trackSelectionHandler(handlerName, onSelectionChange) {
  const handler = FteTrackingItem[handlerName];
  if (!handler || handler.tracksSelection) return;
  const trackedHandler = (item, selected) => {
    handler(item, selected);
    onSelectionChange();
  };
  trackedHandler.tracksSelection = true;
  FteTrackingItem[handlerName] = trackedHandler;
}

function FteTrackingAdmin() {
  const [shownPanel, setShownPanel] = useState(ShownPanelsEnum.None);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [, setSelectionTick] = useState(0);

  const { warnings, refreshWarnings } = useFteWarnings();
  const { searchItem, setSearchItem, changeMonth, refreshSearchItem } = useMonthSelector();

  const createFunction = () => {
    const newItem = new FteTrackingItem();
    newItem.UserId = '';
    newItem.Status = 'Open';
    newItem._showUserSelector = true;
    // The month on screen, not today's: a replacement for a cancelled or reversed row is created while
    // looking at that row's month.
    const y = searchItem.Year;
    const m = String(searchItem.Month).padStart(2, '0');
    const lastDate = new Date(y, searchItem.Month, 0).getDate();
    newItem.StartDateUtc = `${y}-${m}-01`;
    newItem.EndDateUtc = `${y}-${m}-${String(lastDate).padStart(2, '0')}`;
    setSelectedItem(newItem);
    setShownPanel(ShownPanelsEnum.AddNewItem);
  };

  const refresh = useCallback(
    (doRefresh) => {
      setShownPanel(ShownPanelsEnum.None);
      setSelectedItem(null);
      if (doRefresh) {
        refreshSearchItem();
        refreshWarnings();
      }
    },
    [refreshSearchItem, refreshWarnings]
  );

  const createReverseIob = useCallback((entries) => {
    // Validate: all entries must be invoiced
    const nonInvoiced = entries.filter((item) => !item.IoblineItemId);
    if (nonInvoiced.length > 0) {
      ErrorMessage.SetErrorMessage('All selected entries must be invoiced (have an IOB) to create a reverse IOB.');
      return;
    }

    // Validate: no double reversal (mirrors the backend check)
    const alreadyReversed = entries.filter((item) => item.ReversedOnUtc);
    if (alreadyReversed.length > 0) {
      ErrorMessage.SetErrorMessage('Some selected entries have already been reversed and cannot be reversed again.');
      return;
    }

    const reverseItem = createReverseIobItem(entries);
    if (!reverseItem) return;
    setSelectedItem(reverseItem);
    setShownPanel(ShownPanelsEnum.AddNewItem + '_reverse');
  }, []);

  const createReverseBonusIob = useCallback((entries) => {
    // Validate: every entry still carries a bonus share that has not been reversed (mirrors the backend check)
    if (!entries.every((item) => hasLiveBonusShare(item))) {
      ErrorMessage.SetErrorMessage(
        'All selected entries must have an invoiced bonus that has not been reversed yet. ' +
          'Reversing an entry already reverses its bonus.'
      );
      return;
    }

    const reverseItem = createReverseIobItem(entries);
    if (!reverseItem) return;
    setSelectedItem(reverseItem);
    setShownPanel(ShownPanelsEnum.AddNewItem + '_reverseBonus');
  }, []);

  useEffect(() => {
    FteTrackingItem.editFunction = (id, item) => {
      UserService.GetUserList().then((users) => {
        const user = users.find((u) => String(u.userId) === String(item.UserId));
        const userOfficeId = user ? user.officeId : null;
        setSelectedItem({
          ...item,
          _userOfficeId: userOfficeId,
          HiringOfficeId: item.HiringOfficeId || userOfficeId,
        });
        setShownPanel(ShownPanelsEnum.EditItem);
      });
    };
    FteTrackingItem.deleteFunction = (item) => {
      ErrorMessage.SetApprove('Are you sure you want to delete this FTE entry?', () => {
        OutsourcedUserFteService.Delete(item.OutsourcedUserFteid)
          .then((response) => {
            if (response && response.error) {
              ErrorMessage.SetErrorMessage(response.error);
            } else {
              ErrorMessage.SetNotification('FTE entry deleted successfully.');
              refresh(true);
            }
          })
          .catch(() => {
            ErrorMessage.SetErrorMessage('Error! ErrorCode: FTE02. Failed to delete FTE entry. Please notify IT!');
          });
      });
    };
    FteTrackingItem.lockFunction = (item) => {
      ErrorMessage.SetApprove(
        'Are you sure you want to lock this FTE entry? This action is final and irreversible.',
        () => {
          OutsourcedUserFteService.ChangeStatus({ FteEntryIds: [item.OutsourcedUserFteid], TargetStatus: 'Locked' })
            .then((response) => {
              if (response && response.error) {
                ErrorMessage.SetErrorMessage(response.error);
              } else {
                ErrorMessage.SetNotification('FTE entry locked successfully.');
                refresh(true);
              }
            })
            .catch(() => {
              ErrorMessage.SetErrorMessage('Error! ErrorCode: FTE03. Failed to lock FTE entry. Please notify IT!');
            });
        }
      );
    };
    FteTrackingItem.cancelFunction = (item) => {
      ErrorMessage.SetApprove(
        'Are you sure you want to cancel this FTE entry? A cancelled entry can never be invoiced and cannot be reactivated — create a new entry for the month instead.',
        () => {
          OutsourcedUserFteService.ChangeStatus({ FteEntryIds: [item.OutsourcedUserFteid], TargetStatus: 'Cancelled' })
            .then((response) => {
              if (response && response.error) {
                ErrorMessage.SetErrorMessage(response.error);
              } else {
                ErrorMessage.SetNotification('FTE entry cancelled.');
                refresh(true);
              }
            })
            .catch(() => {
              ErrorMessage.SetErrorMessage('Error! ErrorCode: FTE10. Failed to cancel FTE entry. Please notify IT!');
            });
        }
      );
    };
    FteTrackingItem.reverseIobFunction = (item) => {
      createReverseIob([item]);
    };
    FteTrackingItem.reverseBonusIobFunction = (item) => {
      createReverseBonusIob([item]);
    };
  }, [refresh, createReverseIob, createReverseBonusIob]);

  // Track selection changes for the toolbar. Not a one-off: every toolbar action swaps the grid for <Loading />,
  // and the remounted grid binds new handlers over the wrapped ones, after which no tick reached the toolbar.
  useEffect(() => {
    const onSelectionChange = () => setSelectionTick((t) => t + 1);
    trackSelectionHandler('clickOnSelect', onSelectionChange);
    trackSelectionHandler('clickOnGlobalSelect', onSelectionChange);
  });

  const lockEntries = () => {
    const { Year, Month } = searchItem;
    const selectedEntries = itemList.filter((item) => item.Selected);
    const hasSelection = selectedEntries.length > 0;

    const warning = ' This action is final and irreversible, make sure you really want to do it.';
    const confirmMessage = hasSelection
      ? `Are you sure you want to lock ${selectedEntries.length} selected FTE entry/entries?${warning}`
      : `Are you sure you want to lock all open/confirmed FTE entries for ${Year}/${String(Month).padStart(
          2,
          '0'
        )}?${warning}`;

    ErrorMessage.SetApprove(confirmMessage, () => {
      setLoading(true);
      const promise = hasSelection
        ? OutsourcedUserFteService.ChangeStatus({
            FteEntryIds: selectedEntries.map((item) => item.OutsourcedUserFteid),
            TargetStatus: 'Locked',
          })
        : OutsourcedUserFteService.ChangeStatus({ Year, Month, TargetStatus: 'Locked' });

      promise
        .then((response) => {
          if (response && response.error) {
            ErrorMessage.SetErrorMessage(response.error);
            setLoading(false);
          } else {
            ErrorMessage.SetNotification(response || 'Entries locked successfully.');
            setLoading(false);
            refresh(true);
          }
        })
        .catch(() => {
          ErrorMessage.SetErrorMessage('Error! ErrorCode: FTE04. Failed to lock entries. Please notify IT!');
          setLoading(false);
        });
    });
  };

  const generateIobByMonth = () => {
    const { Year, Month } = searchItem;
    const selectedEntries = itemList.filter((item) => item.Selected);
    const hasSelection = selectedEntries.length > 0;

    const request = hasSelection
      ? { FteEntryIds: selectedEntries.map((item) => item.OutsourcedUserFteid) }
      : { Year, Month };

    const confirmMessage = hasSelection
      ? `Generate IOB invoices for ${selectedEntries.length} selected FTE entry/entries?`
      : `Generate IOB invoices for all locked FTE entries in ${Year}/${String(Month).padStart(2, '0')}?`;

    ErrorMessage.SetApprove(confirmMessage, () => {
      setLoading(true);
      OutsourcedUserFteService.GenerateIob(request)
        .then((response) => {
          setLoading(false);
          if (response && response.error) {
            ErrorMessage.SetErrorMessage(response.error);
            return;
          }
          // CD-5466: the request is queued, nothing has changed on this page yet — the entries flip to
          // Invoiced when the background generation finishes and reports back in a popup.
          handleIobQueuedResponse(response);
        })
        .catch((error) => {
          ErrorMessage.SetErrorMessage(
            error?.isBusinessError
              ? error.message
              : 'Error! ErrorCode: FTE06. Failed to generate IOB invoices. Please notify IT!'
          );
          setLoading(false);
        });
    });
  };

  const cancelEntries = () => {
    const selectedEntries = itemList.filter((item) => item.Selected);
    if (selectedEntries.length === 0) {
      ErrorMessage.SetErrorMessage('Please select one or more locked, not yet invoiced FTE entries to cancel.');
      return;
    }

    const confirmMessage =
      `Are you sure you want to cancel ${selectedEntries.length} FTE entry/entries? ` +
      'A cancelled entry can never be invoiced and cannot be reactivated — create a new entry for the month instead.';

    ErrorMessage.SetApprove(confirmMessage, () => {
      setLoading(true);
      OutsourcedUserFteService.ChangeStatus({
        FteEntryIds: selectedEntries.map((item) => item.OutsourcedUserFteid),
        TargetStatus: 'Cancelled',
      })
        .then((response) => {
          setLoading(false);
          if (response && response.error) {
            ErrorMessage.SetErrorMessage(response.error);
          } else {
            ErrorMessage.SetNotification(response || 'Entries cancelled.');
            refresh(true);
          }
        })
        .catch(() => {
          ErrorMessage.SetErrorMessage('Error! ErrorCode: FTE11. Failed to cancel entries. Please notify IT!');
          setLoading(false);
        });
    });
  };

  const generateBonusIobByMonth = () => {
    const { Year, Month } = searchItem;
    const selectedEntries = itemList.filter((item) => item.Selected);
    const hasSelection = selectedEntries.length > 0;

    const request = hasSelection
      ? { Year, Month, FteEntryIds: selectedEntries.map((item) => item.OutsourcedUserFteid) }
      : { Year, Month };

    const confirmMessage = hasSelection
      ? `Generate bonus IOB invoices for ${selectedEntries.length} selected FTE entry/entries?`
      : `Generate bonus IOB invoices from BambooHR for all eligible FTE entries in ${Year}/${String(Month).padStart(
          2,
          '0'
        )}?`;

    ErrorMessage.SetApprove(confirmMessage, () => {
      setLoading(true);
      OutsourcedUserFteService.GenerateBonusIob(request)
        .then((response) => {
          setLoading(false);
          if (response && response.error) {
            ErrorMessage.SetErrorMessage(response.error);
            return;
          }
          // Queued, like the salary generation: nothing has changed on this page yet, so there is nothing
          // to refresh — the bonus invoices appear when the background generation reports back in a popup.
          handleBonusIobQueuedResponse(response);
        })
        .catch(() => {
          ErrorMessage.SetErrorMessage(
            'Error! ErrorCode: FTE12. Failed to generate bonus IOB invoices. Please notify IT!'
          );
          setLoading(false);
        });
    });
  };

  const reverseIobFromSelection = () => {
    const selectedEntries = itemList.filter((item) => item.Selected);
    if (selectedEntries.length === 0) {
      ErrorMessage.SetErrorMessage('Please select one or more invoiced FTE entries to create a reverse IOB.');
      return;
    }
    createReverseIob(selectedEntries);
  };

  const reverseBonusIobFromSelection = () => {
    const selectedEntries = itemList.filter((item) => item.Selected);
    if (selectedEntries.length === 0) {
      ErrorMessage.SetErrorMessage('Please select one or more FTE entries with an invoiced bonus to reverse.');
      return;
    }
    createReverseBonusIob(selectedEntries);
  };

  const openChangeStatusPanel = () => {
    const selected = itemList.filter((item) => item.Selected);
    const hasSelected = selected.length > 0;
    const item = hasSelected
      ? new ChangeStatusItem(
          selected.map((i) => i.OutsourcedUserFteid),
          null,
          null
        )
      : new ChangeStatusItem([], searchItem.Year, searchItem.Month);
    setSelectedItem(item);
    setShownPanel('ChangeStatus');
  };

  const selectedEntries = itemList.filter((item) => item.Selected);
  const selectedCount = selectedEntries.length;
  const hasSelection = selectedCount > 0;
  const allSelectedAreLocked = hasSelection && selectedEntries.every((item) => item.Status === 'Locked');
  const allSelectedAreInvoiced =
    hasSelection && selectedEntries.every((item) => !!item.IoblineItemId && !item.ReversedOnUtc);
  const allSelectedAreCancellable = hasSelection && selectedEntries.every((item) => canEntryBeCancelled(item));
  const allSelectedCanTakeBonus =
    hasSelection &&
    selectedEntries.every((item) => (item.Status === 'Locked' || item.Status === 'Invoiced') && !item.ReversedOnUtc);
  const allSelectedHaveLiveBonus = hasSelection && selectedEntries.every((item) => hasLiveBonusShare(item));

  if (loading) return <Loading />;

  return (
    <div className='contentwrapper fte-tracking-page'>
      <div className='d-flex justify-content-end align-items-start flex-column align-items-end gap-2 mb-3'>
        <FigmaButtonRow
          className={'InnerMenuButtonFigmaList'}
          buttons={[
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Secondary,
              iconPosition: IconPositionEnum.Right,
              onClick: createFunction,
              label: 'Create',
              tooltip: 'Create a new FTE entry',
              roleKey: 'outsourcedfte_edit',
              iconName: 'fa-solid fa-plus-circle',
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Secondary,
              iconPosition: IconPositionEnum.Right,
              onClick: openChangeStatusPanel,
              label: hasSelection ? `Change Status (${selectedCount})` : 'Change Status',
              tooltip: 'Change status of selected or all open/confirmed FTE entries',
              roleKey: 'outsourcedfte_edit',
              iconName: 'fa-solid fa-arrows-rotate',
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Secondary,
              iconPosition: IconPositionEnum.Right,
              onClick: lockEntries,
              label: hasSelection ? `Lock (${selectedCount})` : 'Lock',
              tooltip: 'Lock selected or all open/confirmed FTE entries for the selected month',
              roleKey: 'outsourcedfte_lock',
              iconName: 'fa-solid fa-lock',
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Danger,
              iconPosition: IconPositionEnum.Right,
              onClick: cancelEntries,
              label: hasSelection ? `Cancel (${selectedCount})` : 'Cancel',
              tooltip:
                'Cancel selected locked, not yet invoiced FTE entries — final, frees the FTE capacity for a replacement entry',
              roleKey: 'outsourcedfte_lock',
              iconName: 'fa-solid fa-ban',
              disabled: !allSelectedAreCancellable,
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Right,
              onClick: generateIobByMonth,
              label: hasSelection ? `Generate IOB (${selectedCount})` : 'Generate IOB',
              tooltip: 'Generate IOB invoices for selected or all locked FTE entries',
              roleKey: 'outsourcedfte_iobgenerate',
              iconName: 'fa-solid fa-file-invoice-dollar',
              disabled: hasSelection && !allSelectedAreLocked,
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Right,
              onClick: generateBonusIobByMonth,
              label: hasSelection ? `Generate Bonus IOB (${selectedCount})` : 'Generate Bonus IOB',
              tooltip:
                'Invoice BambooHR bonuses for the selected month — each bonus is invoiced once, split across the FTE entries',
              roleKey: 'outsourcedfte_iobgenerate',
              iconName: 'fa-solid fa-gift',
              disabled: hasSelection && !allSelectedCanTakeBonus,
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Danger,
              iconPosition: IconPositionEnum.Right,
              onClick: reverseIobFromSelection,
              label: hasSelection ? `Reverse IOB (${selectedCount})` : 'Reverse IOB',
              tooltip:
                'Create a reverse IOB for selected invoiced FTE entries, including any bonus shares on them not yet reversed',
              roleKey: 'outsourcedfte_iobreverse',
              iconName: 'fa-solid fa-rotate-left',
              disabled: !allSelectedAreInvoiced,
            },
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Danger,
              iconPosition: IconPositionEnum.Right,
              onClick: reverseBonusIobFromSelection,
              label: hasSelection ? `Reverse Bonus IOB (${selectedCount})` : 'Reverse Bonus IOB',
              tooltip:
                'Reverse only the bonus invoiced on the selected FTE entries — Generate Bonus IOB for the month bills it again',
              roleKey: 'outsourcedfte_iobreverse',
              iconName: 'fa-solid fa-gift',
              disabled: !allSelectedHaveLiveBonus,
            },
          ]}
        />

        <FteToolbar
          warnings={warnings}
          year={searchItem.Year}
          month={searchItem.Month}
          onChangeMonth={changeMonth}
        />
      </div>
      {shownPanel === 'ChangeStatus' && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={selectedItem}
          clickOnCancel={() => refresh(false)}
          clickOnSave={() => refresh(true)}
          windowDescription={EditChangeStatusWindowDescription}
          errorCode={'FTE09'}
        />
      )}
      {shownPanel === ShownPanelsEnum.AddNewItem && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={selectedItem}
          clickOnCancel={() => refresh(false)}
          clickOnSave={() => refresh(true)}
          windowDescription={EditFteTrackingWindowDescription}
          errorCode={'FTE07'}
        />
      )}
      {shownPanel === ShownPanelsEnum.EditItem && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Update}
          editedItem={selectedItem}
          clickOnCancel={() => refresh(false)}
          clickOnSave={() => refresh(true)}
          windowDescription={EditOutsourcedUserFteWindowDescription}
          errorCode={'FTE05'}
        />
      )}
      {shownPanel === ShownPanelsEnum.AddNewItem + '_reverse' && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={selectedItem}
          clickOnCancel={() => refresh(false)}
          clickOnSave={(response) => {
            handleReverseIobResponse(response);
            refresh(true);
          }}
          windowDescription={EditReverseIobWindowDescription}
          errorCode={'FTE08'}
        />
      )}
      {shownPanel === ShownPanelsEnum.AddNewItem + '_reverseBonus' && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={selectedItem}
          clickOnCancel={() => refresh(false)}
          clickOnSave={(response) => {
            handleReverseIobResponse(response);
            refresh(true);
          }}
          windowDescription={EditReverseBonusIobWindowDescription}
          errorCode={'FTE13'}
        />
      )}
      <TabOfDataTable
        type={FteTrackingItem}
        searchParamsType={FteTrackingSearchParams}
        shownPanel={shownPanel}
        serviceFunction={OutsourcedUserFteService.GetListBySearching}
        convertDBItemsFunction={OutsourcedUserFteService.ConvertFteTrackingItems}
        searchItem={searchItem}
        showSearchStrip={true}
        clickOnStartSearch={(newSearchItem) => {
          newSearchItem.Year = searchItem.Year;
          newSearchItem.Month = searchItem.Month;
          setSearchItem({ ...newSearchItem });
        }}
        orderBy='UserName'
        orderDirection='Asc'
        enableUrlPush={false}
        errorCode='FTE50'
        forceClearCacheOnSearch={true}
        isSelectAllDisabled={true}
        changeOfItemListFunction={setItemList}
      />
    </div>
  );
}

export default FteTrackingAdmin;
